import { useEffect, useMemo, useRef } from "react";
import {
    Alert,
    Autocomplete,
    Button as MuiButton,
    CircularProgress,
    Stack,
    TextField,
    Typography as MuiTypography,
    useTheme,
} from "@mui/material";
import { Banner, Typography as FoodsTypography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import { useIngredientSupplierTransports } from "src/api/client-api/ingredientSupplierTransports/useIngredientSupplierTransports";
import { INGREDIENT_LEVEL_STAGES, type TransportStage } from "src/api/client-api/transport/transportStages";
import { useEnabledTransportStages } from "src/api/client-api/transport/useEnabledTransportStages";
import { useTransportCircuits, type TransportCircuit } from "src/api/client-api/transport/useTransportCircuits";
import { FoodsLoadingIndicator, FoodsSelectField } from "src/components/EntityForm/FoodsFields";
import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";
import { useFoodpilotContext } from "src/context/FoodpilotContext";

import type {
    IngredientSupplierTransportChangeReason,
    IngredientSupplierTransportAppearance,
    IngredientSupplierTransportSelection,
} from "./ingredientSupplierTransportTypes";
import { IngredientSupplierTransportSummary } from "./IngredientSupplierTransportPresentation.internal";

type IngredientSupplierTransportFieldsProps = {
    appearance?: IngredientSupplierTransportAppearance;
    disabled?: boolean;
    ingredientSupplierId?: number;
    value?: IngredientSupplierTransportSelection;
    onChange: (next: IngredientSupplierTransportSelection, reason: IngredientSupplierTransportChangeReason) => void;
};

const extractIdFromIri = (iri?: string): number | undefined => {
    if (iri === undefined) {
        return undefined;
    }

    const rawId = iri.split("/").pop();
    const id = rawId === undefined ? NaN : Number.parseInt(rawId, 10);

    return Number.isNaN(id) ? undefined : id;
};

export const IngredientSupplierTransportFields = ({
    appearance = "legacy",
    disabled = false,
    ingredientSupplierId,
    value = {},
    onChange,
}: IngredientSupplierTransportFieldsProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { selectedCompany } = useFoodpilotContext();
    const { isStageEnabled } = useEnabledTransportStages();
    const hydrationScope = `${selectedCompany?.id ?? "none"}:${ingredientSupplierId ?? "new"}`;
    const hydrationScopeRef = useRef(hydrationScope);
    const initializedFromApi = useRef(false);
    const shouldPreserveDraftOnHydration = useRef(true);

    const enabledIngredientStages = useMemo(
        () => INGREDIENT_LEVEL_STAGES.filter((stage) => isStageEnabled(stage)),
        [isStageEnabled],
    );

    const {
        data: transportCircuitsData,
        transportCircuits: loadedTransportCircuits,
        isError: areTransportCircuitsErrored,
        isFetching: areTransportCircuitsFetching,
        isLoading: areTransportCircuitsLoading,
        refetch: refetchTransportCircuits,
    } = useTransportCircuits({
        organizationId: selectedCompany?.id,
        enabled: selectedCompany?.id !== undefined && enabledIngredientStages.length > 0,
    });

    const {
        data: ingredientSupplierTransportsData,
        ingredientSupplierTransports: loadedIngredientSupplierTransports,
        isError: areIngredientSupplierTransportsErrored,
        isFetching: areIngredientSupplierTransportsFetching,
        isLoading: areIngredientSupplierTransportsLoading,
        refetch: refetchIngredientSupplierTransports,
    } = useIngredientSupplierTransports({
        organizationId: selectedCompany?.id,
        ingredientSupplierId,
        enabled:
            selectedCompany?.id !== undefined &&
            ingredientSupplierId !== undefined &&
            enabledIngredientStages.length > 0,
    });
    const transportCircuits = useMemo(() => loadedTransportCircuits ?? [], [loadedTransportCircuits]);
    const ingredientSupplierTransports = useMemo(
        () => loadedIngredientSupplierTransports ?? [],
        [loadedIngredientSupplierTransports],
    );

    const hasUnresolvedCircuit = useMemo(
        () =>
            transportCircuitsData !== undefined &&
            ingredientSupplierTransportsData !== undefined &&
            !areTransportCircuitsFetching &&
            !areIngredientSupplierTransportsFetching &&
            ingredientSupplierId !== undefined &&
            ingredientSupplierTransports.some((row) => {
                const rowIngredientSupplierId =
                    typeof row.ingredientSupplier === "string" ?
                        extractIdFromIri(row.ingredientSupplier)
                    :   row.ingredientSupplier.id;
                if (rowIngredientSupplierId !== ingredientSupplierId) return false;
                if (!(enabledIngredientStages as readonly string[]).includes(row.stage)) return false;
                if (typeof row.transportCircuit !== "string") return false;
                const transportCircuitId = extractIdFromIri(row.transportCircuit);
                return transportCircuits.every((circuit) => circuit.id !== transportCircuitId);
            }),
        [
            areIngredientSupplierTransportsFetching,
            areTransportCircuitsFetching,
            enabledIngredientStages,
            ingredientSupplierId,
            ingredientSupplierTransports,
            ingredientSupplierTransportsData,
            transportCircuits,
            transportCircuitsData,
        ],
    );
    const persistedTransportIds = useMemo(() => {
        const ids: Partial<Record<TransportStage, number>> = {};
        if (ingredientSupplierId === undefined) return ids;

        for (const row of ingredientSupplierTransports) {
            const rowIngredientSupplierId =
                typeof row.ingredientSupplier === "string" ?
                    extractIdFromIri(row.ingredientSupplier)
                :   row.ingredientSupplier.id;
            if (rowIngredientSupplierId !== ingredientSupplierId) continue;
            if (!(enabledIngredientStages as readonly string[]).includes(row.stage)) continue;
            ids[row.stage] = row.id;
        }

        return ids;
    }, [enabledIngredientStages, ingredientSupplierId, ingredientSupplierTransports]);

    useEffect(() => {
        const scopeChanged = hydrationScopeRef.current !== hydrationScope;
        hydrationScopeRef.current = hydrationScope;
        shouldPreserveDraftOnHydration.current = !scopeChanged;
        initializedFromApi.current = false;
    }, [hydrationScope]);

    useEffect(() => {
        if (
            ingredientSupplierId === undefined ||
            initializedFromApi.current ||
            transportCircuitsData === undefined ||
            ingredientSupplierTransportsData === undefined ||
            areTransportCircuitsFetching ||
            areIngredientSupplierTransportsFetching
        ) {
            return;
        }

        const persistedSelections: IngredientSupplierTransportSelection = {};
        for (const row of ingredientSupplierTransports) {
            const rowIngredientSupplierId =
                typeof row.ingredientSupplier === "string" ?
                    extractIdFromIri(row.ingredientSupplier)
                :   row.ingredientSupplier.id;

            if (rowIngredientSupplierId !== ingredientSupplierId) {
                continue;
            }

            if (!(enabledIngredientStages as readonly string[]).includes(row.stage)) {
                continue;
            }

            const rowTransportCircuit = row.transportCircuit;
            const transportCircuit =
                typeof rowTransportCircuit === "string" ?
                    transportCircuits.find((circuit) => circuit.id === extractIdFromIri(rowTransportCircuit))
                :   rowTransportCircuit;

            if (transportCircuit === undefined) {
                continue;
            }

            persistedSelections[row.stage] = {
                id: row.id,
                transportCircuit,
            };
        }

        if (hasUnresolvedCircuit) {
            return;
        }

        const hydratedSelections = { ...persistedSelections };
        if (shouldPreserveDraftOnHydration.current) {
            for (const stage of enabledIngredientStages) {
                const draftSelection = value[stage];
                if (draftSelection === undefined) continue;
                hydratedSelections[stage] = {
                    ...draftSelection,
                    id: draftSelection.id ?? persistedSelections[stage]?.id,
                };
            }
        }

        initializedFromApi.current = true;
        shouldPreserveDraftOnHydration.current = true;
        onChange(hydratedSelections, "hydrate");
    }, [
        areIngredientSupplierTransportsFetching,
        areTransportCircuitsFetching,
        enabledIngredientStages,
        hasUnresolvedCircuit,
        hydrationScope,
        ingredientSupplierId,
        ingredientSupplierTransports,
        ingredientSupplierTransportsData,
        onChange,
        transportCircuits,
        transportCircuitsData,
        value,
    ]);

    if (enabledIngredientStages.length === 0) {
        return null;
    }

    const hasTransportCircuitsData = transportCircuitsData !== undefined;
    const hasIngredientSupplierTransportsData = ingredientSupplierTransportsData !== undefined;
    const hasHydrationError =
        selectedCompany?.id === undefined ||
        (areTransportCircuitsErrored && !hasTransportCircuitsData) ||
        (ingredientSupplierId !== undefined &&
            areIngredientSupplierTransportsErrored &&
            !hasIngredientSupplierTransportsData);
    const hasRefreshError =
        (areTransportCircuitsErrored && hasTransportCircuitsData) ||
        (ingredientSupplierId !== undefined &&
            areIngredientSupplierTransportsErrored &&
            hasIngredientSupplierTransportsData);
    const isHydrating =
        !hasHydrationError &&
        (areTransportCircuitsLoading ||
            areTransportCircuitsFetching ||
            !hasTransportCircuitsData ||
            (ingredientSupplierId !== undefined &&
                (areIngredientSupplierTransportsLoading ||
                    areIngredientSupplierTransportsFetching ||
                    !hasIngredientSupplierTransportsData)));
    const canRetryHydration =
        areTransportCircuitsErrored || (ingredientSupplierId !== undefined && areIngredientSupplierTransportsErrored);
    const retryCollections = async () => {
        const refetches: Promise<unknown>[] = [];
        if (areTransportCircuitsErrored) {
            refetches.push(refetchTransportCircuits());
        }
        if (ingredientSupplierId !== undefined && areIngredientSupplierTransportsErrored) {
            refetches.push(refetchIngredientSupplierTransports());
        }
        await Promise.all(refetches);
    };

    if (hasHydrationError) {
        if (appearance === "legacy") {
            return (
                <Stack sx={{ p: theme.spacing(2) }}>
                    <Alert
                        severity="error"
                        action={
                            canRetryHydration ?
                                <MuiButton color="inherit" onClick={() => void retryCollections()} size="small">
                                    {t("common.actions.refresh", "Refresh")}
                                </MuiButton>
                            :   undefined
                        }
                    >
                        {t("common.error_occurred", "An error occurred")}
                    </Alert>
                </Stack>
            );
        }

        return (
            <Stack sx={{ p: 2 }}>
                <RemoteErrorAlert
                    message={t("common.error_occurred", "An error occurred")}
                    status="server-error"
                    onRetry={canRetryHydration ? retryCollections : undefined}
                />
            </Stack>
        );
    }

    const setStageCircuit = (stage: TransportStage, circuit: TransportCircuit | null) => {
        onChange(
            {
                ...value,
                [stage]: {
                    id: value[stage]?.id ?? persistedTransportIds[stage],
                    transportCircuit: circuit,
                },
            },
            "user",
        );
    };

    return (
        <Stack gap={2} sx={{ p: 2 }}>
            {hasUnresolvedCircuit &&
                (appearance === "product-v2" ?
                    <RemoteErrorAlert
                        message={t(
                            "ingredientSupplier.transport.unresolvedCircuit",
                            "A saved transport circuit is no longer available.",
                        )}
                        status="contract-error"
                    />
                :   <Alert severity="error">
                        {t(
                            "ingredientSupplier.transport.unresolvedCircuit",
                            "A saved transport circuit is no longer available.",
                        )}
                    </Alert>)}
            {hasRefreshError &&
                (appearance === "product-v2" ?
                    <RemoteErrorAlert
                        message={t("common.error_occurred", "An error occurred")}
                        onRetry={retryCollections}
                        retryLabel={t("common.actions.refresh", "Refresh")}
                        status="server-error"
                    />
                :   <Alert
                        severity="error"
                        action={
                            <MuiButton color="inherit" onClick={() => void retryCollections()} size="small">
                                {t("common.actions.refresh", "Refresh")}
                            </MuiButton>
                        }
                    >
                        {t("common.error_occurred", "An error occurred")}
                    </Alert>)}
            {isHydrating && (
                <Stack aria-label={t("common.loading", "Loading...")} gap={theme.spacing(1.5)} role="status">
                    {appearance === "product-v2" ?
                        <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
                    :   <CircularProgress aria-label={t("common.loading", "Loading...")} size={theme.spacing(2)} />}
                </Stack>
            )}
            {appearance === "product-v2" ?
                <FoodsTypography variant="body2" sx={{ color: theme.custom.grey[2000] }}>
                    {t(
                        "ingredientSupplier.transport.helper",
                        "Fill in the required transport circuits for this ingredient-supplier pair.",
                    )}
                </FoodsTypography>
            :   <MuiTypography variant="body2" color="text.secondary">
                    {t(
                        "ingredientSupplier.transport.helper",
                        "Fill in the required transport circuits for this ingredient-supplier pair.",
                    )}
                </MuiTypography>
            }

            {enabledIngredientStages.map((stage) => {
                const selectedCircuit = value[stage]?.transportCircuit ?? null;
                const showRequiredCircuitError = !isHydrating && selectedCircuit === null;

                return appearance === "product-v2" ?
                        <FoodsSelectField
                            appearance="product-v2"
                            disabled={disabled}
                            key={stage}
                            options={transportCircuits}
                            selectedOption={
                                transportCircuits.find((circuit) => circuit.id === selectedCircuit?.id) ??
                                selectedCircuit
                            }
                            getId={(option) => option.id}
                            getName={(option) => option.name ?? `#${option.id}`}
                            onChange={(circuit) => setStageCircuit(stage, circuit ?? null)}
                            required
                            label={t(`transport.stage.${stage}`, stage.replace(/_/g, " "))}
                            errorText={
                                showRequiredCircuitError ?
                                    t("ingredientSupplier.transport.required", "Required circuit")
                                :   undefined
                            }
                            placeholder={t("transport.create.select_circuit", "Select a transport circuit")}
                            searchPlaceholder={t("component.select.searchplaceholder", "Search…")}
                            emptyOptionsText={t("component.select.nooptions", "No option available")}
                            noResultText={t("component.select.noresultfound", "No results found")}
                            noValue={t("component.select.unselectvalue", "Don't select anything")}
                        />
                    :   <Autocomplete
                            disabled={disabled}
                            key={stage}
                            size="small"
                            options={transportCircuits}
                            value={
                                transportCircuits.find((circuit) => circuit.id === selectedCircuit?.id) ??
                                selectedCircuit
                            }
                            getOptionLabel={(option) => option.name ?? `#${option.id}`}
                            isOptionEqualToValue={(option, selected) => option.id === selected.id}
                            onChange={(_event, circuit) => setStageCircuit(stage, circuit)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    required
                                    label={t(`transport.stage.${stage}`, stage.replace(/_/g, " "))}
                                    error={showRequiredCircuitError}
                                    helperText={
                                        showRequiredCircuitError ?
                                            t("ingredientSupplier.transport.required", "Required circuit")
                                        :   undefined
                                    }
                                />
                            )}
                        />;
            })}

            <IngredientSupplierTransportSummary appearance={appearance} value={value} />

            {appearance === "product-v2" ?
                <Banner variant="info">
                    {t(
                        "ingredientSupplier.transport.inheritedByProducts",
                        "This information will be automatically reused in the Transport step for products using this ingredient.",
                    )}
                </Banner>
            :   <Alert severity="info">
                    {t(
                        "ingredientSupplier.transport.inheritedByProducts",
                        "This information will be automatically reused in the Transport step for products using this ingredient.",
                    )}
                </Alert>
            }
        </Stack>
    );
};
